import React from "react";
import {GridRenderCellParams} from "@mui/x-data-grid-pro";
import {useRowEditContext} from "../RowEditContext";
import {SimpleInputEditCell} from "./SimpleInputEditCell";
import {BooleanEditCell} from "./BooleanEditCell";
import {PossibleValueEditCell} from "./PossibleValueEditCell";
import {BlobEditCell} from "./BlobEditCell";
import {NotInGridPlaceholder} from "./NotInGridPlaceholder";
import {GreyedDisplayCell} from "./GreyedDisplayCell";
import {DynamicFormFieldDefinition} from "qqq/components/forms/DynamicFormUtils";

function isNotInGridType(formFieldDef: DynamicFormFieldDefinition): boolean
{
   // CODE_EDITOR adornment maps to "ace" type in DynamicFormUtils.getDynamicField
   if (formFieldDef.type === "ace")
   {
      return true;
   }
   // HTML: QFieldType.HTML = "HTML" — fieldMetaData.type holds the QFieldType enum string value
   const qType = (formFieldDef.fieldMetaData as any)?.type;
   if (qType === "HTML")
   {
      return true;
   }
   // MaterialDashboardFieldMetaData.editableInGrid === false opts a field out of inline grid editing
   const mdbFieldMetaData = (formFieldDef.fieldMetaData as any)?.supplementalFieldMetaData?.get?.("materialDashboard");
   if (mdbFieldMetaData?.editableInGrid === false)
   {
      return true;
   }
   return false;
}

/***************************************************************************
 * Replacement for the default column `renderCell` when row edit is enabled
 * on the grid. Falls back to a caller-supplied display renderer when the
 * cell is NOT in an editing row.
 *
 * Usage: wrap the existing `renderCell` per column:
 *
 *    columnsModel[i].renderCell = (params) => (
 *       <RowEditCellRenderer params={params} displayRenderer={originalRenderCell} />
 *    );
 ***************************************************************************/
interface Props
{
   params: GridRenderCellParams;
   displayRenderer: (params: GridRenderCellParams) => React.ReactNode;
}

export const RowEditCellRenderer: React.FC<Props> = ({params, displayRenderer}) =>
{
   const ctx = useRowEditContext();

   if (!ctx || ctx.editingRowId == null)
   {
      ///////////////////////////////////////////////
      // No row is editing — render normal display //
      ///////////////////////////////////////////////
      return <>{displayRenderer(params)}</>;
   }

   const formFieldDef = ctx.formFieldDefs[params.field];

   if (ctx.editingRowId !== params.id)
   {
      ///////////////////////////////////////////////////////////////////////////////
      // Another row is editing — non-editing rows render normally so the grid     //
      // doesn't look entirely greyed-out / disabled.  Only the editing row itself //
      // shows inputs; its non-editable cells are the only ones greyed below.      //
      ///////////////////////////////////////////////////////////////////////////////
      return <>{displayRenderer(params)}</>;
   }

   // We ARE the editing row.

   if (!formFieldDef || !formFieldDef.isEditable)
   {
      return <GreyedDisplayCell displayValue={displayRenderer(params)} />;
   }

   if (isNotInGridType(formFieldDef))
   {
      return <NotInGridPlaceholder fieldLabel={formFieldDef.label} />;
   }

   if (formFieldDef.possibleValueProps)
   {
      return <PossibleValueEditCell fieldName={params.field} formFieldDef={formFieldDef} />;
   }

   // DynamicFormUtils returns QFieldType enum values (e.g. "DATE", "TIME",
   // "PASSWORD") which are uppercase. Normalise to lowercase so the switch
   // cases below match correctly for all field types.
   switch (formFieldDef.type?.toLowerCase())
   {
      case "checkbox":
         return <BooleanEditCell fieldName={params.field} isEditable={formFieldDef.isEditable} />;
      case "file":
         return <BlobEditCell fieldName={params.field} formFieldDef={formFieldDef} />;
      case "text":
      case "number":
      case "date":
      case "datetime-local":
      case "time":
      case "password":
         return <SimpleInputEditCell fieldName={params.field} formFieldDef={formFieldDef} onBlur={ctx.handleFieldBlur} />;
      default:
         //////////////////////////////////////////////////////////////////
         // Unknown type — fall back to greyed display rather than crash //
         //////////////////////////////////////////////////////////////////
         return <GreyedDisplayCell displayValue={displayRenderer(params)} />;
   }
};
